import { Map, MapEvent } from 'ol';
import { listen, listenOnce } from 'ol/events';
import { containsExtent, Extent, getBottomRight, getTopLeft, scaleFromCenter, equals as equalsExtent } from 'ol/extent';
import { ObjectEvent } from 'ol/Object';
import ObjectEventType from 'ol/ObjectEventType';
import Overlay from 'ol/Overlay';
import View from "ol/View";
import {fromExtent as polygonFromExtent} from "ol/geom/Polygon";
import MapProperty from "ol/MapProperty";
import BaseLayer from 'ol/layer/Base';
import ControlBase, { IControlBaseOptions } from './controlBase';
import { getLocalizedText } from '../common/translations';
import MapEventType from 'ol/MapEventType';

const MAX_RATIO = 1.01; // Maximum width and/or height extent ratio that determines when the overview map should be zoomed out.
const MIN_RATIO = 0.5; // Minimum width and/or height extent ratio that determines when the overview map should be zoomed in.

interface IOverviewMapOptions extends IControlBaseOptions {
    layers: BaseLayer[]
}

export default class CustomOverviewMap extends ControlBase {

    private isTrackingMouse:boolean;
    private viewExtent_:Extent;
    private ovmapDiv_:HTMLElement;
    private ovmap_:Map;
    private boxOverlay_:Overlay;
    ovmapPostrenderKey_: any;

    constructor(options: IOverviewMapOptions) {
 
        options.tooltipText = getLocalizedText("controlOverviewMap");
        options.tooltipLocation = "left";
        options.collapsible = true;
        let svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        // Bootstrap ikona Plus https://icons.getbootstrap.com/icons/plus/
        svg.innerHTML = `<path fill-rule="evenodd" d="M15.817.113A.5.5 0 0 1 16 .5v14a.5.5 0 0 1-.402.49l-5 1a.502.502 0 0 1-.196 0L5.5 15.01l-4.902.98A.5.5 0 0 1 0 15.5v-14a.5.5 0 0 1 .402-.49l5-1a.5.5 0 0 1 .196 0L10.5.99l4.902-.98a.5.5 0 0 1 .415.103zM10 1.91l-4-.8v12.98l4 .8V1.91zm1 12.98 4-.8V1.11l-4 .8v12.98zm-6-.8V1.11l-4 .8v12.98l4-.8z"/>`;
        svg.setAttribute("width", "24px");
        svg.setAttribute("height", "24px");
        svg.setAttribute("viewBox", "0 0 16 16");
        options.symbol = svg;

        super(options);

        this.collapsibleContent.parentElement.insertBefore(this.collapsibleContent, this.button);

        this.viewExtent_ = undefined;

        this.ovmapDiv_ = document.createElement('div');
        this.ovmapDiv_.className = 'ol-overviewmap-map';

        this.ovmap_ = new Map({ 
            controls: [],
            interactions: [],
            layers: options.layers,
            target: this.ovmapDiv_
        });
        const ovmap = this.ovmap_;

        const box = document.createElement('div');
        box.className = 'ol-overviewmap-box';
        box.style.boxSizing = 'border-box';
        let horizontal = document.createElement("div");
        horizontal.classList.add("horizontal-line");
        box.appendChild(horizontal);
        let vertical = document.createElement("div");
        vertical.classList.add("vertical-line");
        box.appendChild(vertical);

        this.boxOverlay_ = new Overlay({
            position: [0, 0],
            positioning: 'center-center',
            element: box,
        });
        this.ovmap_.addOverlay(this.boxOverlay_);

        this.element.classList.add('ol-custom-overviewmap', 'ol-unselectable', 'ol-control');
        this.collapsibleContent.appendChild(this.ovmapDiv_);

        listenOnce(this.ovmap_, "rendercomplete", () => {
            let viewport = this.ovmap_.getViewport();
            viewport.addEventListener("pointerdown", this.startTrackingMouse.bind(this));
            viewport.addEventListener("pointerup", this.stopTrackingMouse.bind(this));
            viewport.addEventListener("pointermove", this.moveBox.bind(this));
        }, this);

        this.button.addEventListener("click", this.renderMapOnToggle.bind(this));
    }
 
    setMap(map:Map) {
        const oldMap = this.getMap();
        if (map === oldMap) {
            return;
        }
        if (oldMap) {
            this.ovmap_.setTarget(null);
        }
        super.setMap(map);

        if (map) {
            this.ovmap_.setTarget(this.ovmapDiv_);
            this.listenerKeys.push(listen(map, ObjectEventType.PROPERTYCHANGE, this.handleMapPropertyChange_, this));

            const view = map.getView();
            if (view) {
                this.bindView_(view);
                if (view.isDef()) {
                    this.ovmap_.updateSize();
                    this.resetExtent_();
                }
            }

            if (!this.ovmap_.isRendered()) {
                this.updateBoxAfterOvmapIsRendered_();
            }
        }
    }

    setCollapsed(isCollapsed:boolean) {
        let currentIsCollapsed = this.container.classList.contains("control-collapsed");
        if (currentIsCollapsed != isCollapsed) {
            this.boundToggleCollapsedState();
            this.renderMapOnToggle();
        }
    }
 
    handleMapPropertyChange_(event:ObjectEvent) {
        if (event.key === MapProperty.VIEW) {
            const newView = this.getMap().getView();
            this.bindView_(newView);
        } else if (!this.ovmap_.isRendered() && (event.key === MapProperty.TARGET || event.key === MapProperty.SIZE)) {
            this.ovmap_.updateSize();
        }
    }
 
    bindView_(view:View) {
        this.ovmap_.setView(new View({
            projection: view.getProjection(),
            center: view.getCenter()
        }));
    }

    private stopTrackingMouse(event :MouseEvent) {
        let shouldUpdate = this.isTrackingMouse;
        this.isTrackingMouse = false;
        if (shouldUpdate) {
            let newCenter = this.ovmap_.getEventCoordinate(event);
            let extent = this.getMap().getView().calculateExtent();
            if (newCenter[0] < extent[0]) {
                newCenter[0] = extent[0];
            }
            if (newCenter[0] > extent[2]) {
                newCenter[0] = extent[2];
            }
            if (newCenter[1] < extent[1]) {
                newCenter[1] = extent[1];
            }
            if (newCenter[1] > extent[3]) {
                newCenter[1] = extent[3];
            }
            this.getMap().getView().setCenterInternal(newCenter);
        }
    }

    private startTrackingMouse(event :MouseEvent) {
        event.preventDefault();
        this.isTrackingMouse = true;
    }

    private moveBox(event :MouseEvent) {
        if (this.isTrackingMouse) {
            let newCenter = this.ovmap_.getEventCoordinate(event);
            this.boxOverlay_.setPosition(newCenter);
        }
    }

    private validateExtent_() {
        const map = this.getMap();
        const ovmap = this.ovmap_;

        if (!map.isRendered() || !ovmap.isRendered()) {
            return;
        }

        const mapSize = map.getSize();
 
        const view = map.getView();
        const extent = view.calculateExtentInternal(mapSize);
 
        if (this.viewExtent_ && equalsExtent(extent, this.viewExtent_)) {
            // repeats of the same extent may indicate constraint conflicts leading to an endless cycle
            return;
        }
        this.viewExtent_ = extent;

        const ovmapSize = ovmap.getSize();

        const ovview = ovmap.getView();
        const ovextent = ovview.calculateExtentInternal(ovmapSize);

        const topLeftPixel = ovmap.getPixelFromCoordinateInternal(getTopLeft(extent));
        const bottomRightPixel = ovmap.getPixelFromCoordinateInternal(getBottomRight(extent));

        const boxWidth = Math.abs(topLeftPixel[0] - bottomRightPixel[0]);
        const boxHeight = Math.abs(topLeftPixel[1] - bottomRightPixel[1]);

        const ovmapWidth = ovmapSize[0];
        const ovmapHeight = ovmapSize[1];

        if (boxWidth < ovmapWidth * MIN_RATIO ||
            boxHeight < ovmapHeight * MIN_RATIO ||
            boxWidth > ovmapWidth * MAX_RATIO ||
            boxHeight > ovmapHeight * MAX_RATIO
        ) {
            this.resetExtent_();
        } else if (!containsExtent(ovextent, extent)) {
            this.recenter_();
        }
    }
 
    private resetExtent_() {
        const map = this.getMap();
        const ovmap = this.ovmap_;

        const mapSize = map.getSize();

        const view = map.getView();
        const extent = view.calculateExtentInternal(mapSize);

        const ovview = ovmap.getView();

        // get how many times the current map overview could hold different
        // box sizes using the min and max ratio, pick the step in the middle used
        // to calculate the extent from the main map to set it to the overview map,
        const steps = Math.log(MAX_RATIO / MIN_RATIO) / Math.LN2;
        const ratio = 1 / (Math.pow(2, steps / 2) * MIN_RATIO);
        scaleFromCenter(extent, ratio);
        ovview.fitInternal(polygonFromExtent(extent) as any);
    }
 
    private recenter_() {
        const map = this.getMap();
        const ovmap = this.ovmap_;
        const view = map.getView();
        const ovview = ovmap.getView();
        ovview.setCenterInternal(view.getCenterInternal());
    }
 
    private updateBox_() {
        const map = this.getMap();
        const ovmap = this.ovmap_;

        if (!map.isRendered() || !ovmap.isRendered()) {
            return;
        }
 
        const mapSize = map.getSize();
        const view = map.getView();
        const ovview = ovmap.getView();
        const rotation = -view.getRotation();
        const overlay = this.boxOverlay_;
        const box = this.boxOverlay_.getElement();
        const center = view.getCenterInternal();
        const resolution = view.getResolution();
        const ovresolution = ovview.getResolution();
        const width = (mapSize[0] * resolution) / ovresolution;
        const height = (mapSize[1] * resolution) / ovresolution;

        // set position using center coordinates
        overlay.setPosition(center);

        // set box size calculated from map extent size and overview map resolution
        if (box) {
            box.style.width = width + 'px';
            box.style.height = height + 'px';
            const transform = 'rotate(' + rotation + 'rad)';
            box.style.transform = transform;
        }
    }
 
    private updateBoxAfterOvmapIsRendered_() {
        if (this.ovmapPostrenderKey_) {
            return;
        }
        this.ovmapPostrenderKey_ = listenOnce(
            this.ovmap_, 
            MapEventType.POSTRENDER, 
            function (event) { 
                delete this.ovmapPostrenderKey_; 
                this.updateBox_();
            }, 
            this
        );
    }
 
    private renderMapOnToggle() {
        // manage overview map if it had not been rendered before and control
        // is expanded
        const ovmap = this.ovmap_;
        if (!this.container.classList.contains("control-collapsed")) {
            if (ovmap.isRendered()) {
                this.viewExtent_ = undefined;
                ovmap.render();
                return;
            }
            ovmap.updateSize();
            this.resetExtent_();
            this.updateBoxAfterOvmapIsRendered_();
        }
    }

    render(mapEvent:MapEvent) {
        this.validateExtent_();
        this.updateBox_();
    }
}